import { EventEmitter, Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { isEmpty, remove } from 'lodash-es';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { RumService } from 'src/app/shared/services/rum.service';
import { environment } from '../../../environments/environment';
import { AccountService, IntercomService, LoadingService } from '../../shared/services';
import { User } from '../models/user.model';
import { UserService } from './user.service';
import { catchError, distinctUntilChanged, filter, map, mapTo, startWith, switchMap, take, shareReplay } from 'rxjs/operators';
import { AccountAttributesEnum, ResultTabsetUrlsEnum, AddonsIdentifiersEnum } from '../../shared/enums';
import { SignupInterface } from '../models/signup.model';
import { AccountApiService, BaseApiService } from '../../shared/services/api';
import { HttpHeaders } from '@angular/common/http';
import { KindeClientService } from './kinde-client.service';
import { AFTER_INCORRECT_IMPERSONATE_EMAIL_FLAG, IMPERSONATED_EMAIL_KEY, SI_PRICING_URL } from '../config';

const SHARE_CHANNEL: string = 'share-channel';
enum SHARE_CHANNEL_MESSAGES {
  SHARE = 'SHARE',
  DATA = 'DATA',
  LOGOUT = 'LOGOUT',
}

export const WHOAMI_LOADING_TAG: string = 'whoami-loading-tag';

enum AuthTokenTypeEnum {
  BEARER = 'Bearer',
  BASIC = 'Basic',
}

export interface TokenInterface {
  key: AuthTokenTypeEnum,
  value: string
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  currentUser$: Observable<User>;
  user: User;
  accountAdmin: string;
  token: any;
  loginFlag: boolean = false;
  czEvent$: EventEmitter<any>;
  isFromLogin = false;

  readonly loggedOut$: Observable<symbol>;
  readonly logoutStarted$: Observable<boolean>;

    private readonly tokenKey = 'authorization';
    private currentUserSubject: BehaviorSubject<User> = new BehaviorSubject<User>(null);
    private readonly loggedOutSubject: Subject<symbol> = new Subject();

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly toastr: ToastrService,
    private readonly accountService: AccountService,
    private readonly userService: UserService,
    private readonly rumService: RumService,
    private readonly baseApiService: BaseApiService,
    private readonly loadingService: LoadingService,
    private readonly kindeClientService: KindeClientService,
    private readonly accountApiService: AccountApiService,
    private readonly intercomService: IntercomService,
  ) {
    this.loggedOut$ = this.loggedOutSubject.asObservable();
    this.logoutStarted$ = this.loggedOut$.pipe(
      mapTo(true),
      startWith(false),
      // Keep last value even if there are no subscribers to avoid flipping back to false on re-subscribe
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.czEvent$ = new EventEmitter();
    this.kindeClientService.isAuthenticated().pipe(
        filter(Boolean),
        distinctUntilChanged(),
    ).subscribe(() => {
        if (this.router.url === '/account/login') {
            this.redirectAfterLogin();
        }
    });

    this.activatedRoute.queryParams.pipe(
        map((params: Params) => params?.['logout']),
        filter(Boolean),
    ).subscribe(() => {
        this.clearLogoutData();
        this.postMessage({ type: SHARE_CHANNEL_MESSAGES.LOGOUT });
    });
  }

  initTabSharing(): void {
    const shareChannel = new BroadcastChannel(SHARE_CHANNEL);

    if (!this.getBasicToken()) {
      this.postMessage({
        type: SHARE_CHANNEL_MESSAGES.SHARE,
      });
    }

    shareChannel.onmessage = ({ data }) => {
      if (data.type === SHARE_CHANNEL_MESSAGES.SHARE && this.getBasicToken()) {
        this.postMessage({
          type: SHARE_CHANNEL_MESSAGES.DATA,
          isRememberMe: this.isRememberChecked(),
          token: this.getBasicToken(),
          user: this.user,
        });
      }

      const isUserChanged: boolean = this.isUserChanged(data.user);
      const isTokenChanged: boolean = this.isBasicTokenChanged(data.token);
      if (data.type === SHARE_CHANNEL_MESSAGES.DATA && (isTokenChanged || isUserChanged)) {
        if (data.isRememberMe) {
          localStorage.setItem('isRemember', JSON.parse(data.isRememberMe));
        }
        if (isTokenChanged) {
          this.setBasicToken(data.token);
        }
        if (isUserChanged) {
          this.setUser(JSON.parse(data.user));
        }

        // TODO: consider another implementation
        if (this.router.url === '/account/login') {
          this.redirectAfterLogin();
        }
      }

      if (data.type === SHARE_CHANNEL_MESSAGES.LOGOUT &&
            !["/hsoauth", "/sfoauth", "/zhoauth", '/hsEnrich'].some((u: string) => data.senderUrl.includes(u))
        ) {
            this.getToken().pipe(take(1)).subscribe((token: TokenInterface) => {
                if (token) {
                    this.onPostMessageLogout();
                }
            })
        }
    };
  }

  private isBasicTokenChanged(token: string): boolean {
    return token && (!this.getBasicToken() || this.getBasicToken() !== token);
  }

  private isUserChanged(userStringified: string): boolean {
    return userStringified && (!this.user || JSON.stringify(this.user) !== userStringified);
  }

  churnZeroEvent(eventName) {
    this.czEvent$.emit(eventName);
  }

  getImpersonatedEmail(): string {
    return sessionStorage.getItem(IMPERSONATED_EMAIL_KEY);
  }

  setImpersonatedEmail(email: string): void {
    sessionStorage.setItem(IMPERSONATED_EMAIL_KEY, email);
  }

  removeImpersonatedEmail(): void {
    sessionStorage.removeItem(IMPERSONATED_EMAIL_KEY);
  }

  setAfterIncorrectImpersonateEmailFlag(): void {
    sessionStorage.setItem(AFTER_INCORRECT_IMPERSONATE_EMAIL_FLAG, 'true');
  }

  getAfterIncorrectImpersonateEmailFlag(): string {
    return sessionStorage.getItem(AFTER_INCORRECT_IMPERSONATE_EMAIL_FLAG);
  }

  removeAfterIncorrectImpersonateEmailFlag(): void {
    sessionStorage.removeItem(AFTER_INCORRECT_IMPERSONATE_EMAIL_FLAG);
  }

    loginSSO(userEmail: string): Observable<void> {
        this.loginFlag = true;
        this.isFromLogin = true;
        return this.kindeClientService.login({ authUrlParams: { login_hint: userEmail } });
    }

    login(loginModel) {
        const authToken: string = btoa(loginModel.userEmail + ':' + loginModel.password);
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Basic ${authToken}`,
        })

        return this.loadingService.loadingWrapper(this.whoami(headers), WHOAMI_LOADING_TAG)
            .toPromise()
            .then(
                (response: any) => {
                    this.loginFlag = true;
                    this.isFromLogin = true;
                    if (this.isTrailExpired(response)) {
                        return;
                    }

                    if (
                        new Date(response.billing_period_start) < new Date() &&
                        new Date(response.billing_period_end) > new Date()
                    ) {
                        this.setUser(response);

                        if (loginModel.remember) {
                            localStorage.setItem('isRemember', 'true');
                        }

                        this.setBasicToken(authToken);
                        this.postMessage({
                          type: SHARE_CHANNEL_MESSAGES.DATA,
                          isRememberMe: this.isRememberChecked(),
                          token: authToken,
                          user: response,
                        });
                        this.redirectAfterLogin();
                    } else {
                        this.toastr.warning(
                            'User does not have permission to login.',
                            'Warning!'
                        );
                    }
                    return '200';
                },
                (error) => {
                    return error;
                }
            );
  }

  private redirectAfterLogin(): void {
    const savedSearchURL = localStorage.getItem('savedSearchURL');
    const companyDetailURL = localStorage.getItem('companyDetailURL');
    const redirectUrl = sessionStorage.getItem('redirectUrl');
    if (redirectUrl) {
      this.router.navigateByUrl(this.convertRedirectUrl(redirectUrl));
    } else if (savedSearchURL !== null) {
      this.router.navigate([savedSearchURL]);
    } else if (companyDetailURL !== null) {
      this.router.navigate([companyDetailURL]);
    } else {
      this.navigateToMainPage();
    }
  }

  private convertRedirectUrl(redirectUrl: string): string {
      return redirectUrl
        .replace('%28', '(')
        .replace('%29', ')');
  }

  /**
   * Check user already valid login
   */
  checkUserAlreadyValidLogin(): Observable<boolean> {
    return this.getToken().pipe(
      take(1),
      map((token: TokenInterface) => {
        if (!token) {
          return false;
        }

        if (token.key === 'Bearer' && token.value) {
          return true;
        }

        const authorization: string = this.getBasicToken();
        if (authorization) {
            if (this.isRememberChecked()) {
                this.setBasicToken(authorization);
            }
            return true;
        } else {
          return false;
        }
      }),
    )
  }

  /**
   * Check Logged User Have All Permissions-
   * check roles has ROLE_APIACCESS_contactcloud
   * check for service_credits has credit_type Combined
   * check for billing_period_start and billing_period_end
   */
  checkLoggedUserHaveAllPermissions(user): boolean {
    if (
      new Date(user.billing_period_start) < new Date() &&
      new Date(user.billing_period_end) > new Date()
    ) {
      this.setUser(user);
      if (!user) {
      this.getAdminUsers();
    }
      return true;
    } else {
      if (new Date(user.billing_period_start) > new Date()) {
        this.toastr.error('Invalid User', 'Error!');
      }
      return false;
    }
  }

  /**
   * Update whoami API fields after changing in related fields
   */
  updateLoggedUserData(): any {
    return new Promise((resolve, reject) => {
      this.whoami().subscribe(
        (response: any) => {
              if (this.isTrailExpired(response)) {
                  reject();
                  return;
              }
          this.setUser(response);
          this.postMessage({
            type: SHARE_CHANNEL_MESSAGES.DATA,
            user: response,
          });
          resolve(response);
        },
        (error: any) => {
          reject(error);
        }
      );
    });
  }

  initUserData(): Observable<User> {
      if (this.currentUserSubject.getValue()) {
          return this.currentUser$.pipe(take(1));
      }

      return this.updateLoggedUserDataObservable();
  }

  updateLoggedUserDataObservable(): Observable<User> {
    if (this.getAfterIncorrectImpersonateEmailFlag()) {
        this.removeAfterIncorrectImpersonateEmailFlag();
        this.toastr.warning('Please use valid email address');
    }

    return this.whoami().pipe(
      switchMap((user: User) => {
        if (this.isTrailExpired(user)) {
          return throwError(user);
        }
        this.setUser(user);
        return of(user);
      }),
      catchError((error) => {
        if ([403, 401].includes(error?.status) && this.getImpersonatedEmail()) {
            this.removeImpersonatedEmail();
            this.setAfterIncorrectImpersonateEmailFlag();
            window.location.reload();
        }
        return throwError(() => error);
      })
    );
  }

  isRememberChecked() {
    return localStorage.getItem('isRemember') === 'true';
  }

  setUser(user: User): void {
    this.user = user;
    this.currentUserSubject.next(user);
    this.userService.setUserData(user);
  }

  setBasicToken(token: string) {
      if (this.isRememberChecked()) {
          localStorage.setItem(this.tokenKey, token);
      } else {
          sessionStorage.setItem(this.tokenKey, token);
      }
  }

  getBasicToken(): string {
      const storedValue = this.isRememberChecked() ? localStorage.getItem(this.tokenKey) : sessionStorage.getItem(this.tokenKey);

      try {
          const parsedValue = JSON.parse(storedValue);

          if (typeof parsedValue === 'object' && parsedValue !== null && 'access_token' in parsedValue) {
              return parsedValue.access_token.authToken;
          }
      } catch (e) {}

      return storedValue;
  }

  getToken(): Observable<TokenInterface> {
    return this.kindeClientService.isAuthenticated().pipe(
        take(1),
        switchMap((isKindeAuthenticated: boolean) => {
          if (isKindeAuthenticated) {
            return this.kindeClientService.getToken().pipe(
                switchMap((token: string) => {
                    if (!token) {
                        return this.silentSSOLoginAndGetToken();
                    }
                    return of(token);
                }),
                catchError(() => this.silentSSOLoginAndGetToken()),
                map((value: string) => this.prepareTokenInterfaceData(AuthTokenTypeEnum.BEARER, value)),
            );
          }
          const basicToken: string = this.getBasicToken();
          if (basicToken) {
            return of(this.prepareTokenInterfaceData(AuthTokenTypeEnum.BASIC, basicToken));
          }
          return of(null);
        })
    );
  }

  private silentSSOLoginAndGetToken(): Observable<void> {
      return this.kindeClientService.login({
          authUrlParams: { prompt: 'none' },
          app_state: {
              redirectTo: window.location.href,
          },
      }).pipe(
          // silent login will redirect user to login page, no need to continue stream
          filter(() => false),
          catchError((error) => {
              this.navigateToLogin();
              return throwError(error);
          })
      )
  }

    logout(skipPostMessage?: boolean): Promise<void> {
        return new Promise((resolve, reject) => {
            this.kindeClientService.isAuthenticated().pipe(
                take(1),
                switchMap((isKindeAuthenticated: boolean) => {
                    if (isKindeAuthenticated) {
                        return this.kindeClientService.logout().pipe(mapTo(false));
                    }
                    return of(true);
                }),
                take(1),
                filter(Boolean),
            ).subscribe(() => {
                this.clearLogoutData();
                this.intercomService.shutDown();
                if (!skipPostMessage) {
                    this.postMessage({ type: SHARE_CHANNEL_MESSAGES.LOGOUT });
                }
                resolve();
            }, () => {
                reject();
            })
        });
  }

  private clearLogoutData(): void {
      this.loggedOutSubject.next(Symbol(''))
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem('isRemember');
      this.user = null;
      this.currentUserSubject.next(null);
      this.userService.clearUserData();
      this.accountService.cleanCachedData();
      sessionStorage.clear();
  }

  navigateToLogin(url?) {
    this.logout().then(() => {
        if (url) {
            sessionStorage.setItem('redirectUrl', url);
        }
        this.router.navigate(['/account/login']).then(() => {
            window.location.reload();
        });
    });
  }

    private onPostMessageLogout(): void {
        this.logout(true).then(() => {
            this.router.navigate(['/']).then(() => {
                window.location.reload();
            });
        });
    }
    navigateToUpgrade() {
        if (this.user.referred_by === 'FREE_TRIAL') {
            window.open(SI_PRICING_URL, '_blank');
        }
        else {
            this.logout();
            this.router.navigate(['upgrade']).then(() => {
                window.location.reload();
            });
        }
    }
  navigateToTrialExpired() {
    this.logout();
    this.router.navigate(['trial-expired']).then(() => {
      window.location.reload();
    });
  }
  navigateToMainPage(): void {
    const favorite = this.user?.user_meta.favoritePage;
    this.router.navigate([favorite ? `results/${favorite}` : 'results']);
  }
  navigateToForgotPassword() {
    this.router.navigate(['/account/forgotPassword']);
  }

  csvExportLimit() {
    return this.hasCsvLargeExportAllowed() ? environment.maxXLExports : environment.maxExports;
  }

  get maxNewsAlertCompaniesCount(): number {
    return environment.maxNewsAlertCompaniesCount;
  }

  get isFreeTrialSignupEnabled(): boolean {
    return environment.isFreeTrialSignupEnabled;
  }

  hasCsvLargeExportAllowed(): boolean {
    return this.user?.user_meta?.csvLargeExportAllowed === '1';
  }

  allowedTiers() {
    if (this.user && this.user.user_meta && this.user.user_meta.allowedTiers) {
      const str = this.user.user_meta.allowedTiers;
      return str
        .split(',')
        .filter((item) => {
          return !isEmpty(item);
        })
        .map((item) => {
          return parseInt(item.trim(), 10);
        });
    } else {
      return [0, 1, 2, 3, 9, 10];
    }
  }

  hasHumanVerifiedAccess() {
    if (this.allowedTiers().length > 0) {
      return this.allowedTiers().indexOf(0) !== -1;
    } else {
      return true;
    }
  }
  hasPendingVerificationAccess() {
    const allTires = this.allowedTiers();
    if (allTires.length > 0) {
      let hasNonZeroTier = false;
      allTires.forEach((tier) => {
        if (tier !== 0) {
          hasNonZeroTier = true;
        }
      });
      return hasNonZeroTier;
    } else {
      return true;
    }
  }

  getPendingTiers() {
    if (this.user && this.user.user_meta && this.user.user_meta.allowedTiers) {
      const allowedTiers = this.allowedTiers();
      if (allowedTiers.length > 0) {
        allowedTiers.forEach((tier, index) => {
          if (tier === 0) {
            allowedTiers.splice(index, 1);
          }
        });
      }
      return allowedTiers;
    } else {
      return [1, 2, 3, 9, 10];
    }
  }
  getHumanVerifiedTiers() {
    if (this.user && this.user.user_meta && this.user.user_meta.allowedTiers) {
      const allowedTiers = this.allowedTiers();
      if (allowedTiers.length > 0) {
        const humantires = remove(allowedTiers, (item) => {
          return item === 0;
        });
        return humantires;
      }
      return [0];
    } else {
      return [0];
    }
  }

  hasBypassClearSelectionAfterExport() {
    return !!(this.user &&
      this.user.user_meta &&
      this.user.user_meta.bypassClearSelectionAfterExport &&
      this.user.user_meta.bypassClearSelectionAfterExport === '1');
  }

  hasEmail(emailCRM) {
    if (this.user && this.user.user_meta && this.user.user_meta[emailCRM]) {
      return this.user.user_meta[emailCRM].length > 0;
    } else {
      return false;
    }
  }

  getEmailAndName(email, name) {
    let emailId = '';
    let fullName = '';
    if (
      this.user.user_meta &&
      this.user.user_meta &&
      this.user.user_meta[email]
    ) {
      emailId = this.user.user_meta[email];
    }
    if (
      this.user.user_meta &&
      this.user.user_meta &&
      this.user.user_meta[name]
    ) {
      fullName = this.user.user_meta[name];
    }
    return { email: emailId, name: fullName };
  }

  isAllowExportOfPendingVerificationContacts(): boolean {
    return this.user?.user_meta?.allowExportOfPendingVerificationContacts === '1';
  }

  getSfAuthUrl() {
    if (this.user.account_sf_domain !== '') {
      let sfURL = this.user.account_sf_domain;
      if (!sfURL.includes('http') || !sfURL.includes('https')) {
        sfURL = 'https://' + sfURL;
      }
      // DAAS-6530
      if (this.user.account_sf_domain.includes('.salesforce.com')) {
        return sfURL + '/services/oauth2/authorize';
      } else {
        return sfURL;
      }
    } else if (
      this.user.user_meta &&
      this.user.user_meta.salesforceSandbox &&
      this.user.user_meta.salesforceSandbox === '1'
    ) {
      return environment.salesforce.sandboxUrl;
    } else {
      return environment.salesforce.authUrl;
    }
  }

  getDyAuthUrl() {
    if (this.user?.account_dy_domain && this.user?.account_dy_domain !== '') {
      let dyAuthURL = this.user.account_dy_domain;
      if (!dyAuthURL.includes('http') || !dyAuthURL.includes('https')) {
        dyAuthURL = ' https://' + dyAuthURL;
      }
      return dyAuthURL;
    } else {
      return false;
    }
    // else if (this.user?.user_meta && this.user.user_meta?.dyInstanceUrl) {
    //     return this.user.user_meta.dyInstanceUrl;
    // }
  }

  whoami(additionalHeaders?: HttpHeaders): Observable<User> {
    return this.accountApiService.whoami(additionalHeaders);
  }


  getAdminUsers(): void {
    this.baseApiService
      .get(`account/users`)
      .subscribe((response: any) => {
        const admin = response.users
          .filter((element) => {
            return element.is_admin === true;
          })
          .map((data) => {
            return data.first_name + ' ' + data.last_name;
          });
        this.accountAdmin = admin.toString();
      });
  }
  refreshToken(code): Observable<any> {
    if (code === 'hubspot_lite') {
      code = 'hubspot';
    }
    return this.baseApiService.post(code.toLowerCase() + `/refreshToken`);
  }

  // TODO: consider creating separate config service
  //       with all max count constants based on user meta data
  getMaxMatchIdsCountConfig(): number {
    return environment.maxMatchIdsV2Count;
  }

  getMaxAsyncExports(): number {
    return environment.maxAsyncExports;
  }

  isTrailExpired(user) {
    if (user.account_attributes.indexOf('trial') > -1 && new Date(user.billing_period_end) < new Date()) {
        this.navigateToTrialExpired();
        return true;
    }
    return false;
  }

private postMessage({ type, token, user, isRememberMe }: { type: SHARE_CHANNEL_MESSAGES, token?: string, user?: User, isRememberMe?: boolean }) {
    const shareChannel = new BroadcastChannel(SHARE_CHANNEL);
    const senderUrl = window.location.href;
    shareChannel.postMessage({
      senderUrl,
      type,
      ...(isRememberMe ? { isRememberMe: JSON.stringify(isRememberMe) } : {}),
      ...(token ? { token: token } : {}),
      ...(user ? { user: JSON.stringify(user) } : {}),
    });
    shareChannel.close();
  }

  private prepareTokenInterfaceData(key: AuthTokenTypeEnum, value: string): TokenInterface {
      return { key, value }
  }

  signup(signupObj: SignupInterface): Observable<null> {
    return this.baseApiService.post<null>('account/trial', signupObj, { excludeImpersonate: true });
  }
}
